import React from 'react';
import { motion } from 'framer-motion';
import { Video, Calendar, Star, ArrowRight, CheckCircle2, ShieldCheck, Award, Users } from 'lucide-react';
import { useCartStore } from '../../store/cartStore';

const astrologers = [
  {
    id: 'ast-1',
    name: 'Dr. Aditya Sharma',
    specialty: 'Vedic Astrology & Vastu',
    experience: '25+ Years',
    rating: 4.9,
    price: 2500,
    image: 'https://cdn.pixabay.com/photo/2015/01/08/18/26/man-593333_1280.jpg',
    reviews: 450
  },
  {
    id: 'ast-2',
    name: 'Maya Varma',
    specialty: 'Tarot & Relationship Expert',
    experience: '12+ Years',
    rating: 4.8,
    price: 1800,
    image: 'https://cdn.pixabay.com/photo/2015/05/31/13/45/working-791849_1280.jpg',
    reviews: 320
  },
  {
    id: 'ast-3',
    name: 'Vikram Malhotra',
    specialty: 'Career & Financial Astrology',
    experience: '18+ Years',
    rating: 5.0,
    price: 3000,
    image: 'https://cdn.pixabay.com/photo/2015/01/08/18/30/entrepreneur-593378_1280.jpg',
    reviews: 580
  }
];

const Consultations: React.FC = () => {
  const { addItem } = useCartStore();

  const handleBook = (astrologer: typeof astrologers[0]) => {
    addItem({
      id: astrologer.id,
      name: `Consultation with ${astrologer.name}`,
      price: astrologer.price,
      quantity: 1,
      image: astrologer.image,
      type: 'service'
    });
    alert(`Added consultation with ${astrologer.name} to cart!`);
  };

  return (
    <div className="min-h-screen bg-midnight py-24 noise-overlay">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-gold text-[10px] font-bold uppercase tracking-[0.3em] mb-6 block">Expert Guidance</span>
            <h1 className="text-5xl md:text-7xl font-serif font-bold mb-8">Live Consultations</h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg font-light leading-relaxed">
              Connect with world-renowned astrologers for personalized guidance on career, relationships, and spiritual growth.
            </p>
          </motion.div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-32">
          {[
            { icon: <Users className="w-5 h-5 text-gold" />, label: "Happy Seekers", value: "50k+" },
            { icon: <Award className="w-5 h-5 text-gold" />, label: "Expert Panel", value: "15+" },
            { icon: <Star className="w-5 h-5 text-gold" />, label: "Avg. Rating", value: "4.9/5" },
            { icon: <ShieldCheck className="w-5 h-5 text-gold" />, label: "Secure Sessions", value: "100%" }
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-8 glass-card">
              <div className="flex justify-center mb-4">{stat.icon}</div>
              <p className="text-2xl font-serif font-bold text-white mb-1">{stat.value}</p>
              <p className="text-[10px] text-gray-600 uppercase tracking-widest">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Process */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-32">
          {[
            { step: "01", title: "Choose Expert", desc: "Select an astrologer based on their specialty and experience." },
            { step: "02", title: "Book Slot", desc: "Pick a date and time that works for you from their calendar." },
            { step: "03", title: "Live Session", desc: "Join a private 45-minute video call for your consultation." }
          ].map((item, idx) => (
            <div key={idx} className="relative p-10 glass-card">
              <span className="absolute top-6 right-8 text-5xl font-serif font-bold text-gold/10">{item.step}</span>
              <h3 className="text-2xl font-serif font-bold mb-4 text-white">{item.title}</h3>
              <p className="text-gray-500 text-sm font-light leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Astrologers List */}
        <div className="space-y-12">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-4xl font-serif font-bold">Our Master Astrologers</h2>
            <div className="hidden md:flex items-center space-x-2 text-gray-500 text-[10px] uppercase tracking-widest">
              <CheckCircle2 className="w-4 h-4 text-gold" />
              <span>Verified Experts</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-10">
            {astrologers.map((ast, idx) => (
              <motion.div
                key={ast.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 md:p-12 flex flex-col md:flex-row gap-12 items-center"
              >
                <div className="w-56 h-56 rounded-[32px] overflow-hidden shrink-0 shadow-2xl shadow-midnight">
                  <img src={ast.image} alt={ast.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-grow space-y-6 text-center md:text-left">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <h3 className="text-3xl font-serif font-bold text-white mb-2">{ast.name}</h3>
                      <p className="text-gold text-sm font-bold uppercase tracking-widest">{ast.specialty}</p>
                    </div>
                    <div className="flex items-center justify-center md:justify-end space-x-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" />)}
                      </div>
                      <span className="text-white font-bold">{ast.rating}</span>
                      <span className="text-gray-600 text-xs">({ast.reviews} reviews)</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-8 border-y border-white/5">
                    <div>
                      <p className="text-gray-600 text-[10px] uppercase tracking-widest mb-2">Experience</p>
                      <p className="text-white font-bold">{ast.experience}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-[10px] uppercase tracking-widest mb-2">Mode</p>
                      <p className="text-white font-bold flex items-center justify-center md:justify-start">
                        <Video className="w-4 h-4 mr-2 text-gold" /> Video Call
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-[10px] uppercase tracking-widest mb-2">Duration</p>
                      <p className="text-white font-bold">45 Minutes</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-[10px] uppercase tracking-widest mb-2">Price</p>
                      <p className="text-gold font-serif font-bold text-xl">₹{ast.price.toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-center md:justify-start gap-3">
                    {['Vedic', 'Numerology', 'Palmistry', 'Prashna', 'Vastu'].map((tag) => (
                      <span key={tag} className="px-4 py-1.5 bg-white/5 rounded-full text-[10px] text-gray-500 border border-white/10 uppercase tracking-widest">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 w-full md:w-auto">
                  <button
                    onClick={() => handleBook(ast)}
                    className="btn-primary w-full md:w-auto group"
                  >
                    Book Session
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Consultations;
